import { Splide } from '@splidejs/splide';
import { AutoScroll } from '@splidejs/splide-extension-auto-scroll';
import { Grid } from '@splidejs/splide-extension-grid';

/**
 * Initialise Splide carousels inside any element matching `containerSelector`.
 * Handles AutoScroll/Grid extensions, the progress bar, and applies the W3C
 * APG carousel ARIA pattern (overriding Splide's default tabs pattern).
 *
 * @param {string} containerSelector CSS selector for the block wrapper, e.g. `.wp-block-knightowl-blocks-slider`.
 */
export function initSplide(containerSelector) {
	document.addEventListener('DOMContentLoaded', () => {
		const splideContainers = document.querySelectorAll(containerSelector);

		for (const container of splideContainers) {
			const splides = container.querySelectorAll('.splide');

			for (const splideElement of splides) {
				setupSplideInstance(splideElement);
			}
		}
	});
}

function setupSplideInstance(splideElement) {
	const shouldAutoScroll = splideElement.dataset.scroll === 'true';
	const shouldUseGrid = splideElement.dataset.grid === 'true';

	const extensions = {};
	if (shouldAutoScroll) extensions.AutoScroll = AutoScroll;
	if (shouldUseGrid) extensions.Grid = Grid;

	const splideInstance = new Splide(splideElement);
	const bar = splideElement.querySelector('.slider-progress-bar');

	if (bar) {
		splideInstance.on('mounted move', () => {
			const end = splideInstance.Components.Controller.getEnd() + 1;
			const rate = Math.min((splideInstance.index + 1) / end, 1);
			bar.style.width = `${100 * rate}%`;
		});
	}

	setupCarouselAria(splideInstance);

	splideInstance.mount(extensions);
}

function setupCarouselAria(splideInstance) {
	const updateAriaCurrent = () => {
		const root = splideInstance.root;
		root.querySelectorAll('.splide__pagination__page').forEach(btn => {
			btn.removeAttribute('aria-selected');
			if (btn.classList.contains('is-active')) {
				btn.setAttribute('aria-current', 'true');
			} else {
				btn.removeAttribute('aria-current');
			}
		});
	};

	const setupAria = () => {
		const root = splideInstance.root;

		root.setAttribute('role', 'region');
		root.setAttribute('aria-roledescription', 'carousel');

		const slides = root.querySelectorAll('.splide__slide');
		slides.forEach((slide, i) => {
			slide.setAttribute('role', 'group');
			slide.setAttribute('aria-roledescription', 'slide');
			if (!slide.hasAttribute('aria-label')) {
				slide.setAttribute('aria-label', `${i + 1} of ${slides.length}`);
			}
		});

		const pagination = root.querySelector('.splide__pagination');
		if (pagination) {
			pagination.setAttribute('role', 'group');
		}

		root.querySelectorAll('.splide__pagination__page').forEach(btn => {
			btn.setAttribute('role', 'button');
		});

		updateAriaCurrent();
	};

	splideInstance.on('mounted refresh updated', setupAria);
	splideInstance.on('moved', updateAriaCurrent);
}
